import { useSearchParams } from 'react-router-dom';

import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import { useTenant } from '@/providers/TenantProvider';

export type CustomerRecord = {
  customerId: string;
  name: string;
};

export interface CustomersResponse {
  customers: CustomerRecord[];
  error: Error | null;
  isFetching: boolean;
  isLoading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
}

export type CustomerSortKey = keyof CustomerRecord;
export type SortDir = 'asc' | 'desc';

type Props = {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  query?: string;
  sortKey?: CustomerSortKey;
  sortDir?: SortDir;
};

type CatalogCustomersApiResponse = {
  items?: { cust_id?: string; customer_name?: string }[];
  limit?: number;
  total?: number;
};

// API will throw an error if "limit" > 100
const MAX_CATALOG_CUSTOMERS_PAGE_SIZE = 100;

export function useCustomers({
  enabled = true,
  page = 1,
  pageSize = 25,
  query: queryFromProps,
  sortKey = 'name',
  sortDir = 'asc',
}: Props): CustomersResponse {
  const { tenantAlias } = useTenant();
  const [searchParams] = useSearchParams();
  const query = queryFromProps ?? searchParams.get('query') ?? '';
  const safePageSize = Math.min(pageSize, MAX_CATALOG_CUSTOMERS_PAGE_SIZE);
  const offset = Math.max(0, (page - 1) * safePageSize);

  const queryResult = useApiQuery<CatalogCustomersApiResponse>(
    tenantAlias,
    ApiEndpoint.CatalogCustomers,
    {
      query: {
        offset,
        limit: safePageSize,
        include_total: true,
        include_attrs: true,
        query,
      },
    },
    { enabled, placeholderData: (prev) => prev },
  );

  const { items = [], limit, total = 0 } = queryResult?.data ?? {};
  const customers: CustomerRecord[] = items.map((item) => ({
    customerId: item.cust_id ?? '',
    name: item.customer_name ?? '',
  }));
  const sortedCustomers = [...customers].sort((a, b) => {
    const aValue = a[sortKey] ?? '';
    const bValue = b[sortKey] ?? '';
    const comparison = aValue.localeCompare(bValue);
    return sortDir === 'desc' ? -comparison : comparison;
  });

  return {
    customers: sortedCustomers,
    error: queryResult.error,
    isFetching: queryResult.isFetching,
    isLoading: queryResult.isLoading,
    page,
    pageSize: limit ?? safePageSize,
    totalCount: total,
  };
}
